/** Parties-stage rich rendering — full Kaycoo prompt schema. */
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PartyCard } from './PartyCard';
import type { PartyVerification } from './PartyVerificationBadge';
import { ConfidenceBadge } from './ConfidenceBadge';

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asText = (v: unknown): string => (typeof v === 'string' ? v : '');
const asBool = (v: unknown): boolean => Boolean(v);
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

export function RichPartiesBody({ output }: { output: Record<string, unknown> }) {
  const parties = asArray(output.parties);
  // Index-aligned with `parties` — verify_parties returns one entry per party.
  const verifications = asArray(output.party_verification) as PartyVerification[];
  const thirdParties = asArray(output.third_parties_referenced);
  const guarantors = asArray(output.guarantors);
  const flags = asArray(output.flags_for_downstream).map((x) => (typeof x === 'string' ? x : ''));
  const notes = asText(output.extraction_notes);
  const relationshipType = asText(output.relationship_type);
  const score = output.parties_confidence_score;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {relationshipType && (
          <Badge variant="outline" className="text-xs">{relationshipType}</Badge>
        )}
        {asBool(output.power_of_attorney_mentioned) && (
          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200">
            Power of attorney
          </Badge>
        )}
        {asBool(output.joint_and_several_liability) && (
          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200">
            Joint &amp; several liability
          </Badge>
        )}
        {asBool(output.indemnification_exists) && (
          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200">
            Indemnification clause present
          </Badge>
        )}
        <span className="ml-auto">
          <ConfidenceBadge score={score} rationale={notes} />
        </span>
      </div>

      {parties.length === 0 ? (
        <Alert>
          <AlertDescription className="text-xs">
            No parties identified by the agent.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-2">
          {parties.map((p, i) => (
            <PartyCard key={i} party={obj(p)} verification={verifications[i]} />
          ))}
        </div>
      )}

      {guarantors.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/30">
          <p className="font-medium text-amber-800 dark:text-amber-300 mb-1">
            Guarantors ({guarantors.length})
          </p>
          <ul className="text-xs space-y-0.5">
            {guarantors.map((g, i) => {
              const o = obj(g);
              return (
                <li key={i}>
                  <span className="font-medium">{asText(o.name) || '—'}</span>
                  {asText(o.guaranteeing_for) && (
                    <span className="text-muted-foreground"> · for {asText(o.guaranteeing_for)}</span>
                  )}
                  {asText(o.guarantee_scope) && (
                    <span className="text-muted-foreground"> · scope: {asText(o.guarantee_scope)}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {thirdParties.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Third parties referenced ({thirdParties.length})
          </p>
          <ul className="text-xs space-y-0.5">
            {thirdParties.map((tp, i) => {
              const o = obj(tp);
              return (
                <li key={i} className="border-b py-0.5">
                  <span className="font-medium">{asText(o.name) || '—'}</span>
                  {asText(o.role) && (
                    <span className="text-muted-foreground"> · {asText(o.role)}</span>
                  )}
                  {asText(o.relevance) && (
                    <div className="text-muted-foreground">{asText(o.relevance)}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {flags.map((f, i) => (
            <span
              key={i}
              className="text-[10px] rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 px-1.5 py-0.5"
            >
              {f}
            </span>
          ))}
        </div>
      )}

      {notes && (
        <p className="text-xs italic text-muted-foreground border-l-2 pl-2">
          Extraction notes: {notes}
        </p>
      )}
    </div>
  );
}
